// useWaitlistManager.ts
// Custom hook to manage client waitlist with automatic polling

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getClientWaitlistByLocalId } from '../../../../../crud/clients_api/client_waitlist';

interface WaitlistData {
  waitlists: any[];
  sorted_mesas: any[];
}

interface UseWaitlistManagerProps {
  hosterMode: boolean;
  pollInterval?: number; // in milliseconds, defaults to 5 minutes
  enabled?: boolean; // Control whether to fetch data
}

interface UseWaitlistManagerReturn {
  waitList: WaitlistData;
  setWaitList: (waitList: WaitlistData) => void;
}

const DEFAULT_POLL_INTERVAL = 300000; // 5 minutes

export const useWaitlistManager = ({
  hosterMode,
  pollInterval = DEFAULT_POLL_INTERVAL,
  enabled = true,
}: UseWaitlistManagerProps): UseWaitlistManagerReturn => {
  const [waitList, setWaitList] = useState<WaitlistData>({ 
    waitlists: [], 
    sorted_mesas: [] 
  });

  // Initial fetch when hosterMode is enabled AND enabled flag is true
  useEffect(() => {
    if (!hosterMode || !enabled) return;
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user?.local_id ?? 0;

    getClientWaitlistByLocalId(localId)
      .then((response: any) => {
        setWaitList(response);
      })
      .catch(() => {
        toast.error('Error obteniendo lista de espera');
      });
  }, [hosterMode, enabled]);

  // Polling: Update waitlist every X minutes when hosterMode is active AND enabled
  useEffect(() => {
    if (!hosterMode || !enabled) return;
    
    let timeoutId: NodeJS.Timeout;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user?.local_id ?? 0;

    const fetchWaitlist = () => {
      getClientWaitlistByLocalId(localId)
        .then((response: any) => {
          setWaitList(response);
        })
        .catch(() => {
          toast.error('Error obteniendo lista de espera');
        })
        .finally(() => {
          timeoutId = setTimeout(fetchWaitlist, pollInterval);
        });
    };

    fetchWaitlist(); // Initial call

    return () => clearTimeout(timeoutId);
  }, [hosterMode, pollInterval, enabled]);

  return {
    waitList,
    setWaitList,
  };
};
